import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Loader2,
  Settings,
  RefreshCw,
  ShoppingBag,
  Users,
  Check,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const WEEKDAYS_FULL = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];

const PLATFORMS = [
  { value: "telegram", label: "Telegram" },
  { value: "privacy", label: "Privacy" },
  { value: "onlyfans", label: "OnlyFans" },
  { value: "twitter", label: "X / Twitter" },
  { value: "instagram", label: "Instagram" },
];

const AgendaPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState("telegram");
  
  // Settings form
  const [availabilityType, setAvailabilityType] = useState("all_days");
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);
  const [specificDays, setSpecificDays] = useState("");
  const [maxPerDay, setMaxPerDay] = useState(3);

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [availRes, exchangesRes] = await Promise.all([
        axios.get(`${API}/availability?Authorization=Bearer ${token}`),
        axios.get(`${API}/exchanges?status=scheduled&Authorization=Bearer ${token}`)
      ]);
      setAvailability(availRes.data.availability || []);
      setExchanges(exchangesRes.data.exchanges || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveAvailability = async () => {
    try {
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      
      const data = {
        platform: selectedPlatform,
        availability_type: availabilityType,
        weekdays: availabilityType === "weekdays" ? selectedWeekdays : [],
        specific_days: availabilityType === "specific_days" 
          ? specificDays.split(",").map(d => parseInt(d.trim())).filter(d => !isNaN(d))
          : [],
        max_per_day: maxPerDay,
        month,
        year
      };

      await axios.post(`${API}/availability?Authorization=Bearer ${token}`, data);
      toast.success("Disponibilidade salva!");
      setShowSettingsModal(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao salvar");
    }
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    const startPadding = (firstDay.getDay() + 6) % 7; // Monday start
    
    // Previous month padding
    for (let i = startPadding - 1; i >= 0; i--) {
      const d = new Date(year, month, -i);
      days.push({ date: d, currentMonth: false });
    }
    
    // Current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), currentMonth: true });
    }
    
    // Next month padding
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), currentMonth: false });
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return exchanges.filter(ex => 
      ex.requester_date === dateStr || ex.target_date === dateStr
    );
  };

  const isDateAvailable = (date) => {
    // Check platform availability
    const platformAvail = availability.find(a => a.platform === selectedPlatform);
    if (!platformAvail) return true;

    const dayOfWeek = (date.getDay() + 6) % 7; // 0=Mon
    const dayOfMonth = date.getDate();

    switch (platformAvail.availability_type) {
      case "all_days":
        return true;
      case "weekdays":
        return platformAvail.weekdays?.includes(dayOfWeek);
      case "specific_days":
        return platformAvail.specific_days?.includes(dayOfMonth);
      default:
        return true;
    }
  };

  const getDateStatus = (date) => {
    const events = getEventsForDate(date);
    const available = isDateAvailable(date);
    
    if (!available) return "unavailable";
    if (events.length === 0) return "available";
    
    const platformAvail = availability.find(a => a.platform === selectedPlatform);
    const maxAllowed = platformAvail?.max_per_day || 3;
    
    if (events.length >= maxAllowed) return "full";
    return "partial";
  };

  const navigateMonth = (delta) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + delta);
    setCurrentDate(newDate);
  };

  const days = getDaysInMonth(currentDate);
  const monthName = currentDate.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  const getPhoto = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return `${process.env.REACT_APP_BACKEND_URL}${photo}`;
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 py-4 bg-[#080808]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/discovery")}
              className="text-white/70"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Minha Agenda</h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowSettingsModal(true)}
            className="text-white/70"
          >
            <Settings className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-24 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Platform Filter */}
          <div className="mb-6">
            <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
              <SelectTrigger className="w-full md:w-[200px] bg-white/5 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-white/10">
                {PLATFORMS.map(p => (
                  <SelectItem key={p.value} value={p.value} className="text-white hover:bg-white/10">
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#FD5564]" />
            </div>
          ) : (
            <>
              {/* Calendar */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6 mb-6">
                {/* Month Navigation */}
                <div className="flex items-center justify-between mb-6">
                  <Button variant="ghost" size="icon" onClick={() => navigateMonth(-1)}>
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <h2 className="text-lg font-semibold capitalize">{monthName}</h2>
                  <Button variant="ghost" size="icon" onClick={() => navigateMonth(1)}>
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>

                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {WEEKDAYS.map(day => (
                    <div key={day} className="text-center text-sm text-white/50 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Days Grid */}
                <div className="grid grid-cols-7 gap-1">
                  {days.map((day, index) => {
                    const status = day.currentMonth ? getDateStatus(day.date) : "unavailable";
                    const events = getEventsForDate(day.date);
                    const isToday = day.date.toDateString() === new Date().toDateString();
                    const isSelected = selectedDate?.toDateString() === day.date.toDateString();

                    return (
                      <motion.button
                        key={index}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => day.currentMonth && setSelectedDate(day.date)}
                        className={`
                          aspect-square rounded-lg flex flex-col items-center justify-center relative
                          transition-all text-sm
                          ${!day.currentMonth ? 'text-white/20' : ''}
                          ${isToday ? 'ring-2 ring-[#FD5564]' : ''}
                          ${isSelected ? 'bg-[#FD5564] text-white' : ''}
                          ${status === "available" && !isSelected ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : ''}
                          ${status === "partial" && !isSelected ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30' : ''}
                          ${status === "full" && !isSelected ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : ''}
                          ${status === "unavailable" && !isSelected ? 'bg-white/5 text-white/30' : ''}
                        `}
                        disabled={!day.currentMonth}
                      >
                        <span>{day.date.getDate()}</span>
                        {events.length > 0 && day.currentMonth && (
                          <div className="absolute bottom-1 flex gap-0.5">
                            {events.slice(0, 3).map((_, i) => (
                              <div key={i} className="w-1 h-1 rounded-full bg-current" />
                            ))}
                          </div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>

                {/* Legend */}
                <div className="flex flex-wrap items-center justify-center gap-4 mt-6 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-emerald-500/50" />
                    <span className="text-white/50">Disponível</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-yellow-500/50" />
                    <span className="text-white/50">Parcial</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-red-500/50" />
                    <span className="text-white/50">Lotado</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-white/20" />
                    <span className="text-white/50">Indisponível</span>
                  </div>
                </div>
              </div>

              {/* Selected Date Events */}
              {selectedDate && (
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 md:p-6">
                  <h3 className="text-lg font-semibold mb-4">
                    {selectedDate.toLocaleDateString('pt-BR', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'long' 
                    })}
                  </h3>

                  {getEventsForDate(selectedDate).length > 0 ? (
                    <div className="space-y-3">
                      {getEventsForDate(selectedDate).map(event => (
                        <div 
                          key={event.id}
                          className="bg-white/5 border border-white/10 rounded-xl p-4"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10">
                              {getPhoto(event.other_user?.profile_photo) ? (
                                <img 
                                  src={getPhoto(event.other_user?.profile_photo)} 
                                  alt="" 
                                  className="w-full h-full object-cover" 
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Users className="w-5 h-5 text-white/30" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <RefreshCw className="w-4 h-4 text-[#FD5564]" />
                                <span className="font-medium">Troca com {event.other_user?.display_name}</span>
                              </div>
                              <div className="text-sm text-white/50">
                                {event.is_requester ? event.requester_platform : event.target_platform}
                              </div>
                            </div>
                            <Badge className="bg-blue-500/20 text-blue-400 border-0">
                              Agendado
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-white/50">
                      {isDateAvailable(selectedDate) ? (
                        <p>Nenhum evento agendado para este dia</p>
                      ) : (
                        <p>Dia não disponível para {selectedPlatform}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar Disponibilidade</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Plataforma
              </label>
              <Select value={selectedPlatform} onValueChange={setSelectedPlatform}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  {PLATFORMS.map(p => (
                    <SelectItem key={p.value} value={p.value} className="text-white hover:bg-white/10">
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Tipo de Disponibilidade
              </label>
              <Select value={availabilityType} onValueChange={setAvailabilityType}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1a] border-white/10">
                  <SelectItem value="all_days" className="text-white hover:bg-white/10">
                    Todos os dias
                  </SelectItem>
                  <SelectItem value="weekdays" className="text-white hover:bg-white/10">
                    Dias da semana específicos
                  </SelectItem>
                  <SelectItem value="specific_days" className="text-white hover:bg-white/10">
                    Dias específicos do mês
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {availabilityType === "weekdays" && (
              <div>
                <label className="block text-sm font-medium text-white/70 mb-3">
                  Selecione os dias
                </label>
                <div className="flex flex-wrap gap-2">
                  {WEEKDAYS_FULL.map((day, index) => (
                    <label 
                      key={day}
                      className={`
                        flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all
                        ${selectedWeekdays.includes(index) 
                          ? 'bg-[#FD5564] text-white' 
                          : 'bg-white/5 text-white/70 hover:bg-white/10'}
                      `}
                    >
                      <Checkbox
                        checked={selectedWeekdays.includes(index)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedWeekdays([...selectedWeekdays, index]);
                          } else {
                            setSelectedWeekdays(selectedWeekdays.filter(d => d !== index));
                          }
                        }}
                        className="hidden"
                      />
                      {day.substring(0, 3)}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {availabilityType === "specific_days" && (
              <div>
                <label className="block text-sm font-medium text-white/70 mb-2">
                  Dias do mês (separados por vírgula)
                </label>
                <Input
                  placeholder="Ex: 5, 10, 15, 20, 25"
                  value={specificDays}
                  onChange={(e) => setSpecificDays(e.target.value)}
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">
                Máximo de divulgações por dia
              </label>
              <Input
                type="number"
                min={1}
                max={10}
                value={maxPerDay}
                onChange={(e) => setMaxPerDay(parseInt(e.target.value) || 1)}
                className="bg-white/5 border-white/10 text-white w-24"
              />
            </div>

            <Button
              className="w-full bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90"
              onClick={saveAvailability}
            >
              <Check className="w-4 h-4 mr-2" />
              Salvar Configurações
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AgendaPage;
